import {
  addToaster,
  getGenericEntityListingAPI,
  hideLoader,
  selectEPRPatient,
  showLoader,
  useDispatch,
} from '@app-store'
import { Accordion, CustomButton, FormInput, SkeletonLoader } from '@ui-kit'
import { useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import { NAMESPACES } from '../../utils/constants'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import { useAppSelector } from '../../utils/agent.store'
import { EprRetryManualPharmacyTaskProps } from '../../types/workflow.types'

const EprRetryManualPharmacyTask = ({
  task,
  recordId,
  isDisabled,
}: EprRetryManualPharmacyTaskProps) => {
  const [initialLoadShimmer, setInitialLoadShimmer] = useState<boolean>(true)
  const [pharmacyIdData, setPharmacyIdData] = useState<FieldValues>()
  const [patientId, setPatientId] = useState<number>(0)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )

  const dispatch = useDispatch()
  const { register, setValue } = useForm({
    defaultValues: {
      pharmacy_id: '',
    },
  })

  const fetchPharmacyData = async () => {
    try {
      const newPayload = {
        select: ['id', 'patient'],
        criteria: [],
        related_entities: {
          patient: {
            select: ['patient_pharmacy'],
            criteria: [],
            related_entities: {
              patient_pharmacy: {
                select: ['pharmacy_id', 'is_epr_retry_success', 'status', 'id'],
                filters: [
                  {
                    criteria: [
                      {
                        field_name: 'status',
                        operation: 'EQUALS',
                        values: ['ACTIVE'],
                      },
                    ],
                  },
                ],
              },
            },
          },
        },
        entity_name: workflowDefinition?.global_variables?.entity_name ?? '',
        entity_id: workflowDefinition?.global_variables?.entity_id ?? '',
      }

      const caseIdObject2 = await getGenericEntityListingAPI(
        newPayload,
        recordId ?? ''
      )
      setPatientId(caseIdObject2?.patient?.id ?? 0)
      const activeStatusData = caseIdObject2?.patient?.patient_pharmacy?.find(
        (item: FieldValues) => item?.status === 'ACTIVE'
      )
      setPharmacyIdData(activeStatusData)
      setValue('pharmacy_id', activeStatusData?.pharmacy_id ?? '')
    } catch (error) {
      console.log(error)
    }
  }
  const updatePharmacyData = async (pharmacy_id: number) => {
    try {
      const data = {
        pharmacy_id: pharmacy_id,
        is_epr_retry_success: 'True',
        _state: 'CHANGED',
      }
      await editEntityDataEmbeddedAPI(
        task?.identifier?.entity_name ?? '',
        pharmacyIdData?.id ?? '',
        data,
        ['pharmacy_id', 'is_epr_retry_success']
      )
      dispatch(
        addToaster({
          id: uuid(),
          type: 'success',
          message: NAMESPACES.PHARMACY_ID_UPDATED,
        })
      )
    } catch (error) {
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: NAMESPACES.SOMETHING_WENT_WRONG,
        })
      )
      console.error(error)
    }
  }
  const eprButtonClickHandler = async () => {
    try {
      dispatch(showLoader())

      const response = await selectEPRPatient(+recordId, patientId)

      if (response?.data?.body?.pharmacyId) {
        setValue('pharmacy_id', response?.data?.body?.pharmacyId ?? '')
        await updatePharmacyData(+response?.data?.body?.pharmacyId)
      } else {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'error',
            message: NAMESPACES.EPR_RETRY_FAILED,
          })
        )
      }
    } catch (err) {
      console.error(err)
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const error = (
          err as {
            response: {
              status: number
              data: { message: string; code: string }
            }
          }
        ).response

        dispatch(
          addToaster({
            id: uuid(),
            type: 'error',
            message: error?.data?.message,
          })
        )
      }
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchViewRecordData = async () => {
    try {
      setInitialLoadShimmer(true)
      await fetchPharmacyData()
    } catch (err) {
      console.error(err)
    } finally {
      setInitialLoadShimmer(false)
    }
  }

  useEffect(() => {
    if (workflowDefinition && recordId) fetchViewRecordData()
  }, [workflowDefinition, recordId])

  return initialLoadShimmer ? (
    <SkeletonLoader />
  ) : (
    <Accordion
      heading={NAMESPACES.CAPTURE_PHARMACY_ID}
      isDefaultActive
      alwaysOpen
    >
      <form className="p2">
        <div className={`p-2 flex justify-end gap-3 `}>
          <CustomButton
            isFilled
            disabled={isDisabled}
            type="button"
            onClick={eprButtonClickHandler}
          >
            {NAMESPACES.INITIATE_PATIENT_SELECT}
          </CustomButton>
        </div>

        <div className="flex flex-row ">
          <div className={`p-4 w-1/3`}>
            <FormInput
              name="pharmacy_id"
              label="Pharmacy ID"
              disabled={true}
              register={register}
            />
          </div>
        </div>
      </form>
    </Accordion>
  )
}

export default EprRetryManualPharmacyTask
